import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ActionDialog from "../../components/ActionDialog";

const BookingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as any;
  const booking = state?.booking || {};

  const [openReject, setOpenReject] = useState(false);
  const [openPropose, setOpenPropose] = useState(false);
  const [openHold, setOpenHold] = useState(false);
  const [reason, setReason] = useState('');
  const [newTime, setNewTime] = useState('');
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);

  const openActions = (e: React.MouseEvent<HTMLElement>) => setActionAnchor(e.currentTarget);
  const closeActions = () => setActionAnchor(null);

  return (
    <>
      <div className="main_title">
        <h1>Booking Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - {" "}
          <button onClick={() => navigate("/bookings")}>Bookings</button> - Detail
        </p>
      </div>

      <div className="cards view_page">
        <div className="table_header">
          <h2>Summary</h2>
          <div className="right_s">
            <Button className="btn btn_outline sm" onClick={openActions} endIcon={<MoreVertIcon />}>Actions</Button>
            <Menu
              anchorEl={actionAnchor}
              open={Boolean(actionAnchor)}
              onClose={closeActions}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { navigate('/bookings', { replace: true }); closeActions(); }}>Accept</MenuItem>
              <MenuItem onClick={() => { setReason(''); setOpenReject(true); closeActions(); }}>Reject</MenuItem>
              <MenuItem onClick={() => { setOpenPropose(true); closeActions(); }}>Propose New Time</MenuItem>
              <MenuItem onClick={() => { setOpenHold(true); closeActions(); }}>Hold</MenuItem>
              <MenuItem onClick={() => { navigate('/bookings', { replace: true }); closeActions(); }}>Cancel</MenuItem>
            </Menu>
          </div>
        </div>
        <div className="gap_p">
          <div className="w_33">
            <h6>Booking ID</h6>
            <p>{booking?.bookingId || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Customer</h6>
            <p>{booking?.customer || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Project</h6>
            <p>{booking?.project || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Product</h6>
            <p>{booking?.product || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Date/Time</h6>
            <p>{booking?.dateTime || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Payment Status</h6>
            <p>{booking?.paymentStatus || '-'}</p>
          </div>
        </div>
      </div>

      <div className="cards view_page">
        <div className="table_header">
          <h2>Linked</h2>
        </div>
        <div className="gap_p">
          <div className="w_33">
            <h6>Linked RFQ</h6>
            <p>{booking?.rfqId ? booking.rfqId : '—'}</p>
          </div>
          <div className="w_33">
            <h6>Delivery Schedule</h6>
            <p>{booking?.schedule || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Vehicle Assignment (ERP)</h6>
            <p>{booking?.vehicle || '-'}</p>
          </div>
        </div>
      </div>

      {/* Reject Dialog */}
      <ActionDialog
        open={openReject}
        onClose={() => setOpenReject(false)}
        title="Reject Booking"
        helperText="Share a quick reason so the customer understands the decision."
        confirmLabel="Reject"
        confirmDisabled={!reason.trim()}
        onConfirm={() => setOpenReject(false)}
        confirmButtonProps={{ className: "btn btn_secondary" }}
      >
        <TextField
          label="Reason"
          placeholder="Enter rejection reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          sx={{
            '& textarea': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>

      {/* Propose New Time Dialog */}
      <ActionDialog
        open={openPropose}
        onClose={() => setOpenPropose(false)}
        title="Propose New Time"
        helperText="Suggest an alternative slot for the delivery."
        confirmLabel="Send"
        confirmDisabled={!newTime}
        onConfirm={() => setOpenPropose(false)}
        confirmButtonProps={{ className: "btn btn_primary" }}
      >
        <TextField
          type="datetime-local"
          label="New Date/Time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </ActionDialog>

      {/* Hold Dialog */}
      <ActionDialog
        open={openHold}
        onClose={() => setOpenHold(false)}
        title="Hold Booking"
        helperText="Pause the booking while you confirm next steps."
        confirmLabel="Hold"
        onConfirm={() => setOpenHold(false)}
        confirmButtonProps={{ className: "btn btn_outline" }}
      >
        <Box sx={{ p: 2, borderRadius: 2, background: '#f8fafc', color: '#475569', fontSize: '0.9rem' }}>
          Booking will be paused until further action.
        </Box>
      </ActionDialog>
    </>
  );
};

export default BookingDetailPage;
